import { useEffect, useRef } from 'react';
import { InfoLogs } from '@/constants/logs';
import { useLatestRef } from '@/hooks/useLatestRef';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import type { ThreeDSSecurityState } from '@/types/context';
import type { AppchargeErrorType, BasePaymentResponse } from '@/types/payment';
import { logInfo } from '@/utils/logger';
import { readPendingChallenge } from '@/utils/pendingChallengeStore';

interface UseResumePendingChallengeParams {
  /** `FeatureFlag.ADYEN_3DS_CHALLENGE_RESUME`. */
  isEnabled: boolean;
  /**
   * Whether checkout data has landed. The challenge form resolves its
   * `paymentMethodId` and Adyen config out of `paymentMethodsSettings`, so
   * resuming before those exist would mount a challenge that cannot poll or
   * initialise.
   */
  isCheckoutReady: boolean;
  on3DSRequired: (state: ThreeDSSecurityState) => void;
  onSuccess: (result: BasePaymentResponse, provider: ProviderEnum) => void;
  onError: (appChargeErrorType?: AppchargeErrorType, provider?: ProviderEnum, rawReason?: string) => void;
  /**
   * Mirrors the live path, which tears the in-flight payment down before
   * surfacing the error. Without it the checkout stays blocked behind a payment
   * that has already failed.
   */
  stopPaymentProcess: () => void;
  /**
   * Lets the caller re-establish the payment state it would have recorded when
   * the payment first started — the method, which both outcome handlers stamp
   * onto the SDK events publishers consume, and the in-flight flags that keep
   * the checkout from being interactive behind a payment that is still
   * resolving. All of it died with the page.
   */
  onResumed?: (activeMethod?: PaymentMethodEnum) => void;
}

/**
 * Picks a 3DS challenge back up on a page that is not the one it started on.
 *
 * A stored record can only exist if a previous page mounted a challenge and
 * then stopped running without ever settling it — `settleChallenge` clears the
 * record on every exit. That is the discard case: Spanish SCA is app-based, so
 * the tab is backgrounded for the whole challenge and Chrome discards it under
 * memory pressure, taking the order-state watchdog with it.
 *
 * Production shows the payment behind that challenge stays open at Adyen for
 * four hours before it is expired, so there is a wide window in which a
 * returning shopper can still finish it. Re-showing the challenge is all this
 * does: the form re-creates the component from the same response, and its
 * order-state watchdog polls `GET /order` independently, so a challenge the
 * issuer already resolved out-of-band settles from the webhook within one poll.
 */
export const useResumePendingChallenge = ({
  isEnabled,
  isCheckoutReady,
  on3DSRequired,
  onSuccess,
  onError,
  stopPaymentProcess,
  onResumed,
}: UseResumePendingChallengeParams): void => {
  // A challenge is resumed at most once per page. Without this a resumed
  // challenge that is dismissed would be re-opened by the next render that
  // re-runs this effect, trapping the shopper in a modal they cannot leave.
  const hasResumedRef = useRef(false);

  // Read through a ref so the effect does not depend on callback identity.
  // Callers pass inline arrows, which are new on every render; depending on
  // them re-ran this effect constantly, and since it returns without arming
  // `hasResumedRef` when there is no record, the first render after a live
  // challenge saved one resumed the challenge already on screen.
  const callbacksRef = useLatestRef({ on3DSRequired, onSuccess, onError, stopPaymentProcess, onResumed });

  useEffect(() => {
    if (!isEnabled || !isCheckoutReady || hasResumedRef.current) return;

    const pending = readPendingChallenge();
    if (!pending) return;

    // Only a record written by a page that is no longer running can be
    // resumed. `timeOrigin` is when THIS page started, so anything saved after
    // it belongs to a challenge that is still on screen — re-mounting that
    // would put a second challenge, and a second CReq, in front of the issuer.
    // This is the real invariant; the identity fix above only stops the effect
    // churning that made it observable.
    if (pending.savedAt >= performance.timeOrigin) return;

    hasResumedRef.current = true;

    const { on3DSRequired, onSuccess, onError, stopPaymentProcess, onResumed } = callbacksRef.current;

    logInfo(InfoLogs.adyen3dsChallengeResumed, {
      orderId: pending.orderId,
      paymentMethodId: pending.paymentMethodId,
      activeMethod: pending.activeMethod,
      msSincePersisted: Date.now() - pending.savedAt,
      // Chrome sets this on a page restored after it discarded the tab, which
      // separates a browser-killed challenge from one whose page went away for
      // any other reason (manual reload, publisher navigation).
      wasDiscarded: Boolean((document as Document & { wasDiscarded?: boolean }).wasDiscarded),
    });

    onResumed?.(pending.activeMethod);

    // These two must be supplied. The form's settle path falls back to the
    // Adyen service's handlers, which are no-op stubs until something registers
    // them, and a freshly booted page has registered nothing and holds no
    // pending card actions either. Omitting them resumes a challenge that can
    // complete and then silently do nothing — the same dead end this exists to
    // fix. They mirror the live payment path in `usePaymentResults`.
    const params = pending.params as BasePaymentResponse;

    on3DSRequired({
      show3DSForm: true,
      provider: ProviderEnum.ADYEN,
      paymentMethod: pending.activeMethod,
      params,
      handlePaymentSuccess: () => onSuccess(params, ProviderEnum.ADYEN),
      handlePaymentError: (_appChargeErrorType?: AppchargeErrorType, rawReasonFrom3DS?: string) => {
        // Ordered as in the live path: tear the in-flight payment down first,
        // or the checkout stays blocked behind a payment that already failed.
        stopPaymentProcess();
        onError(
          (params as { appChargeErrorType?: AppchargeErrorType }).appChargeErrorType,
          ProviderEnum.ADYEN,
          rawReasonFrom3DS
        );
      },
    } as ThreeDSSecurityState);
    // `callbacksRef` is a ref object and so never changes identity; it is listed
    // only because the rule cannot see that through a custom hook.
  }, [isEnabled, isCheckoutReady, callbacksRef]);
};
